import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { FileSideResizeHandle } from '@/components/documents/FileSideResizeHandle';
import { CollapsedToolRail } from '@/components/journal/ToolsColumn';
import { Button } from '@/components/ui/Button';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

const ICON = 1.75;

/**
 * One report side column. Wide screens use the journal rail when closed and
 * the file slider when open. Narrow screens keep the stacked card.
 */
export function ReportToolColumn({
  wide,
  edge,
  open,
  onOpenChange,
  title,
  hint,
  hintClassName,
  icon,
  collapseLabel,
  resizeLabel,
  width,
  min,
  max,
  onWidthChange,
  onWidthReset,
  testId,
  stackClassName,
  bodyClassName,
  children,
}: {
  wide: boolean;
  /** Start is the left column (grows right). End is the right column (grows left). */
  edge: 'start' | 'end';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  hint?: string;
  hintClassName?: string;
  icon: ReactNode;
  collapseLabel: string;
  resizeLabel: string;
  width: number;
  min: number;
  max: number;
  onWidthChange: (width: number) => void;
  onWidthReset: () => void;
  testId: string;
  stackClassName: string;
  bodyClassName?: string;
  children: ReactNode;
}) {
  const hintNode = hint ? (
    <p className={cn(TYPE.small, 'mt-s1 text-ds-muted', hintClassName)}>{hint}</p>
  ) : null;
  const body = <div className={cn('min-w-0', bodyClassName)}>{children}</div>;

  if (!wide) {
    return (
      <aside className={stackClassName} data-testid={`${testId}-panel`}>
        <h3 className={cn(TYPE.section, 'text-ds-text')}>{title}</h3>
        {hintNode}
        {body}
      </aside>
    );
  }

  if (!open) {
    return (
      <CollapsedToolRail
        testId={`${testId}-rail`}
        expanded={false}
        label={title}
        icon={icon}
        onClick={() => onOpenChange(true)}
      />
    );
  }

  return (
    <aside className="jw-hist" data-testid={`${testId}-panel`} aria-label={title}>
      <div className="jw-col-head">
        <div className="jw-col-h">
          <h2 className="min-w-0 flex-1">{title}</h2>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0"
            aria-label={collapseLabel}
            onClick={() => onOpenChange(false)}
          >
            {edge === 'start' ? (
              <ChevronLeft strokeWidth={ICON} />
            ) : (
              <ChevronRight strokeWidth={ICON} />
            )}
          </Button>
        </div>
      </div>
      {hintNode}
      {body}
      <FileSideResizeHandle
        side={edge === 'start' ? 'files' : 'marking'}
        testId={`${testId}-resize`}
        value={width}
        min={min}
        max={max}
        label={resizeLabel}
        onChange={onWidthChange}
        onReset={onWidthReset}
      />
    </aside>
  );
}
